import CustomLoadingButton from 'Components/CustomLoadingButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import TicketSaleDetails from 'Services/SalesManagement/Features/TicketSale/Components/TicketSaleDetails'
import TicketSaleQRCode from 'Services/SalesManagement/Features/TicketSale/Components/TicketSaleQRCode/TicketSaleQRCode'
import {
  useGetSingleTicketSaleQuery,
  useSendTicketSaleEmailReceiptMutation
} from 'Services/SalesManagement/Features/TicketSale/ticketSaleApi'
import { TicketSaleTabType } from 'Services/SalesManagement/Features/TicketSale/types'
import { DRAWER_TAB_PARAM_KEY } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface TicketSaleDrawerProps {
  open: boolean
  handleClose: () => void
  selectedTicketSaleId: string | undefined
}

export default function TicketSaleDrawer({
  open,
  handleClose,
  selectedTicketSaleId
}: Readonly<TicketSaleDrawerProps>) {
  const { confirm } = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const drawerTabFromParams = searchParams.get(DRAWER_TAB_PARAM_KEY) ?? TicketSaleTabType.DETAILS
  const { data: singleTicketSale, isFetching: isSingleTicketSaleLoading } =
    useGetSingleTicketSaleQuery(selectedTicketSaleId ?? '', {
      skip: !selectedTicketSaleId
    })

  const [sendTicketSaleEmailReceipt, { isLoading: isSendEmailReceiptLoading }] =
    useSendTicketSaleEmailReceiptMutation()

  const handleDrawerTabChange = (_event: React.SyntheticEvent, newValue: TicketSaleTabType) => {
    searchParams.set(DRAWER_TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleSendEmailReceipt = () => {
    if (!selectedTicketSaleId) {
      return
    }
    sendTicketSaleEmailReceipt(selectedTicketSaleId)
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Email receipt successfully sent.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`Failed to send the email receipt.`)
      })
  }

  return (
    <DrawerRight
      open={open}
      onClose={handleClose}
      topBar={
        <Box>
          <CustomLoadingButton
            isLoading={isSendEmailReceiptLoading}
            title={t`Send the receipt to target email`}
            onClick={() =>
              confirm({
                title: t`Are you sure?`,
                message: t`Are you sure you want to send the receipt to target email?`,
                handleConfirm: () => {
                  handleSendEmailReceipt()
                }
              })
            }
            size="small"
          >
            {t`Send receipt`}
          </CustomLoadingButton>
        </Box>
      }
    >
      <WithLoader loading={isSingleTicketSaleLoading}>
        {singleTicketSale && (
          <TabContext value={drawerTabFromParams}>
            <TabList onChange={handleDrawerTabChange} aria-label={t`Ticket sale tab list`}>
              <Tab label={t`Details`} value={TicketSaleTabType.DETAILS} />
              <Tab label={t`QR Code`} value={TicketSaleTabType.QR_CODE} />
            </TabList>
            <Divider />
            <TabPanel sx={{ p: 0 }} value={TicketSaleTabType.DETAILS}>
              <TicketSaleDetails ticketSale={singleTicketSale} />
            </TabPanel>
            <TabPanel sx={{ p: 0 }} value={TicketSaleTabType.QR_CODE}>
              <TicketSaleQRCode ticketSale={singleTicketSale} />
            </TabPanel>
          </TabContext>
        )}
      </WithLoader>
    </DrawerRight>
  )
}
